import React, {useEffect, useMemo, useState, useCallback} from "react";
import {
    Card,
    CardBody,
    CardHeader,
    Container,
    Button,
} from "reactstrap";
import TableContainer from "../../Components/Common/TableContainer";
import DeleteModal from "../../Components/Common/DeleteModal";
import {useDispatch, useSelector} from "react-redux";
import {getCustomers, addCustomer, updateCustomer, deleteCustomer} from "../../slices/customers/thunk";
import {getCatalogs, getDescendants} from "../../slices/catalogs/thunk";
import {getCompanies} from "../../slices/companies/thunk";
import CustomerModal, {CustomerModalMode} from "./Components/CustomerModal";
import BreadCrumb from "../../Components/Common/BreadCrumb";

// Definir tipos para las columnas
type ColumnDef = {
    header: string;
    accessorKey?: string;
    accessorFn?: (row: any) => any;
    id?: string;
    cell?: (cellProps: any) => React.ReactNode;
};

// Helper para decodificar el token
const decodeJwtPayload = (token: string) => {
    try {
        const base64Url = token.split('.')[1];
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
            return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        }).join(''));
        return JSON.parse(jsonPayload);
    } catch (e) {
        return null;
    }
};

const CustomerList = () => {
    const [deleteModal, setDeleteModal] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
    const [customerModalOpen, setCustomerModalOpen] = useState(false);
    const [customerModalMode, setCustomerModalMode] = useState<CustomerModalMode>("view");
    const [isSavingCustomer, setIsSavingCustomer] = useState(false);
    const [documentTypes, setDocumentTypes] = useState<any[]>([]);
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [isSuperAdmin, setIsSuperAdmin] = useState(false);
    const [pagination, setPagination] = useState({ page: 1, limit: 10 });
    const [searchTerm, setSearchTerm] = useState('');

    const dispatch = useDispatch<any>();

    const listCustomer = useSelector((state: any) => state.Customers?.customerLists || []);
    const customersMeta = useSelector((state: any) => state.Customers?.meta || { total: 0, page: 1, pageSize: 10 });
    const loading = useSelector((state: any) => state.Customers?.loading || false);
    const catalogList = useSelector((state: any) => state.Catalogs?.catalogList || []);
    const catalogsState = useSelector((state: any) => state.Catalogs || {});
    const descendants = useMemo(() => catalogsState.descendants || {}, [catalogsState.descendants]);
    const companies = useSelector((state: any) => state.Companies?.companyList || []);

    useEffect(() => {
        const authUser = localStorage.getItem("authUser");
        if (authUser) {
            try {
                const parsedUser = JSON.parse(authUser);
                if (parsedUser.token) {
                    const decoded = decodeJwtPayload(parsedUser.token);
                    if (decoded) {
                        // Determinar si es superadmin
                        const userRoles = decoded.roles?.map((role: any) => role.name) || [];
                        setIsSuperAdmin(userRoles.includes('Super Admin'));
                        
                        if (decoded.companyId) {
                            setCompanyId(decoded.companyId);
                        }
                    }
                }
            } catch (error) {
                console.error("Error parsing authUser from localStorage", error);
            }
        }
    }, []);

    useEffect(() => {
        dispatch(getCatalogs());
    }, [dispatch]);

    useEffect(() => {
        if (isSuperAdmin) {
            dispatch(getCompanies());
        }
    }, [dispatch, isSuperAdmin]);

    // useEffect único y controlado para cargar clientes
    useEffect(() => {
        if (companyId) {
            dispatch(getCustomers({
                companyId, 
                isCustomer: true,
                page: pagination.page,
                limit: pagination.limit,
                search: searchTerm
            }));
        }
    }, [dispatch, companyId, pagination.page, pagination.limit, searchTerm]);

    const loadCustomers = () => {
        if (companyId) {
            dispatch(getCustomers({
                companyId, 
                isCustomer: true,
                page: pagination.page,
                limit: pagination.limit,
                search: searchTerm
            }));
        }
    };

    useEffect(() => {
        if (!catalogList || catalogList.length === 0) return;

        const identificationTypeCatalog = catalogList.find(
            (c: any) => c.name === 'Tipos de identificación'
        );

        if (!identificationTypeCatalog) return;

        const catalogId = identificationTypeCatalog.id;

        if (!descendants[catalogId]) {
            dispatch(getDescendants(catalogId));
        } else {
            setDocumentTypes(descendants[catalogId]);
        }

    }, [catalogList, descendants, dispatch]);

    const onClickDelete = useCallback((customer: any) => {
        setSelectedCustomer(customer);
        setDeleteModal(true);
    }, []);

    const handleDeleteCustomer = async () => {
        if (selectedCustomer && companyId) {
            await dispatch(deleteCustomer(selectedCustomer.id || selectedCustomer._id));
            loadCustomers();
        }
        setSelectedCustomer(null);
        setDeleteModal(false);
    };

    const openViewCustomer = useCallback((customer: any) => {
        setSelectedCustomer(customer);
        setCustomerModalMode("view");
        setCustomerModalOpen(true);
    }, []);

    const openEditCustomer = useCallback((customer: any) => {
        setSelectedCustomer(customer);
        setCustomerModalMode("edit");
        setCustomerModalOpen(true);
    }, []);

    const openAddCustomer = () => {
        setSelectedCustomer(null);
        setCustomerModalMode("add");
        setCustomerModalOpen(true);
    };

    const closeCustomerModal = () => {
        setCustomerModalOpen(false);
        setSelectedCustomer(null);
        setIsSavingCustomer(false);
    };

    const handleSubmitCustomer = async (payload: any) => {
        if (!companyId) {
            console.error("No se puede guardar el cliente sin un ID de compañía.");
            return;
        }
        setIsSavingCustomer(true);

        const finalPayload = {...payload, companyId, isCustomer: true};

        const action = customerModalMode === "add"
            ? addCustomer(finalPayload)
            : updateCustomer({customer: {...finalPayload, id: selectedCustomer.id}});

        try {
            const resultAction = await dispatch(action);

            if (addCustomer.fulfilled.match(resultAction) || updateCustomer.fulfilled.match(resultAction)) {
                await loadCustomers();
                closeCustomerModal();
            } else {
                setIsSavingCustomer(false);
                console.error("La operación fue rechazada:", resultAction.payload);
            }
        } catch (error) {
            setIsSavingCustomer(false);
            console.error("Error inesperado al guardar el cliente:", error);
        }
    };

    // Función para obtener el nombre de la compañía
    const getCompanyName = useCallback((companyId: string) => {
        const company = companies.find((c: any) => c.id === companyId);
        return company ? company.tradeName || company.businessName : 'N/A';
    }, [companies]);

    // Manejadores para paginación y búsqueda
    const handlePageChange = (page: number) => {
        setPagination(prev => ({ ...prev, page: page + 1 })); // Convertir de 0-based a 1-based para la API
    };

    const handleSearch = (searchText: string) => {
        setSearchTerm(searchText);
        setPagination(prev => ({ ...prev, page: 1 })); // Resetear a primera página
    };

    const columns = useMemo(
        () => {
            const baseColumns: ColumnDef[] = [
                {header: "Nombre", accessorKey: "name"},
                {header: "Documento", accessorKey: "identificationNumber"},
                {header: "Correo electrónico", accessorKey: "email"},
                {header: "Teléfono", accessorKey: "phone"},
            ];

            // Agregar columna de compañía solo para superadmin
            if (isSuperAdmin) {
                baseColumns.splice(1, 0, {
                    header: "Compañía",
                    accessorKey: "companyId",
                    cell: (cellProps: any) => (
                        <span 
                            className="badge bg-light text-dark"
                            style={{fontSize: '0.875em', fontWeight: '500'}}
                        >
                            {getCompanyName(cellProps.getValue())}
                        </span>
                    )
                });
            }

            baseColumns.push({
                header: "Acciones",
                id: "actions",
                cell: (cellProps: any) => (
                    <div className="d-flex gap-2">
                        <Button
                            color="primary"
                            size="sm"
                            title="Ver"
                            onClick={() => openViewCustomer(cellProps.row.original)}
                        >
                            <i className="ri-eye-line"></i>
                        </Button>

                        <Button
                            color="warning"
                            size="sm"
                            title="Editar"
                            onClick={() => openEditCustomer(cellProps.row.original)}
                        >
                            <i className="ri-pencil-line"></i>
                        </Button>

                        <Button
                            color="danger"
                            size="sm"
                            title="Eliminar"
                            onClick={() => onClickDelete(cellProps.row.original)}
                        >
                            <i className="ri-delete-bin-line"></i>
                        </Button>
                    </div>
                ),
            });

            return baseColumns;
        },
        [onClickDelete, openViewCustomer, openEditCustomer, isSuperAdmin, getCompanyName]
    );

    document.title = "Clientes | Vuelo";

    return (
        <React.Fragment>
            <DeleteModal show={deleteModal} onDeleteClick={handleDeleteCustomer}
                         onCloseClick={() => setDeleteModal(false)}/>
            <div className="page-content">
                <Container fluid>
                    <BreadCrumb title="Clientes" pageTitle="Inicio"/>
                    <Card>
                        <CardHeader className="d-flex justify-content-between align-items-center">
                            <h5 className="card-title mb-0">Clientes</h5>
                            <Button color="primary" onClick={openAddCustomer} disabled={!companyId}>
                                <i className="ri-add-line align-bottom me-1"></i> Agregar Cliente
                            </Button>
                        </CardHeader>
                        <CardBody>
                            <CustomerModal
                                isOpen={customerModalOpen}
                                mode={customerModalMode}
                                customer={selectedCustomer}
                                isSaving={isSavingCustomer}
                                onClose={closeCustomerModal}
                                onSubmit={handleSubmitCustomer}
                                documentTypes={documentTypes}
                            />
                            <TableContainer
                                columns={columns}
                                data={listCustomer || []}
                                isGlobalFilter={true}
                                customPageSize={pagination.limit}
                                theadClass="table-light text-muted"
                                SearchPlaceholder="Buscar por nombre, correo o documento..."
                                divClass="table-responsive table-card mb-1"
                                tableClass="align-middle table-nowrap"
                                isCustomPagination={true}
                                pageCount={Math.ceil(customersMeta.total / customersMeta.pageSize)}
                                totalCount={customersMeta.total}
                                pageIndex={pagination.page - 1}
                                onPageChange={handlePageChange}
                                onSearch={handleSearch}
                                loading={loading && !isSavingCustomer}
                            />
                        </CardBody>
                    </Card>
                </Container>
            </div>
        </React.Fragment>
    );
};

export default CustomerList;
